import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import ComponentCard from "../../common/ComponentCard";
import Label from "../../form/Label";
import InputField from "../../form/input/InputField";
import Select from "../../form/Select";
import MultiSelectCheckbox from "../../form/MultiSelectCheckbox";
import Switch from "../../form/switch/Switch";
import Button from "../../ui/button/Button";
import Alert from "../../ui/alert/Alert";
import api from "../../../services/api";
import authService from "../../../services/authService";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";

interface OfferApi {
    id: string;
    name: string;
}

interface Product {
    id: string;
    name: string;
}

interface Showcase {
    showcaseId: string;
    name: string;
}

export default function NewSellyoMembers() {
    const navigate = useNavigate();
    const companyId = authService.getCompanyId();

    // Form state
    const [name, setName] = useState("");
    const [productId, setProductId] = useState("");
    const [selectedShowcaseIds, setSelectedShowcaseIds] = useState<string[]>([]);
    const [active, setActive] = useState(true);
    const [defaultPassword, setDefaultPassword] = useState("Sellyo@2026");
    const [showPassword, setShowPassword] = useState(false);

    // Offers state
    const [offersList, setOffersList] = useState<string[]>([]);
    const [selectedOffers, setSelectedOffers] = useState<string[]>([]);
    const [selectAllOffers, setSelectAllOffers] = useState(false);
    const [offers, setOffers] = useState<{ value: string; label: string }[]>([]);
    const [loadingOffers, setLoadingOffers] = useState(false);

    // Data state
    const [products, setProducts] = useState<{ value: string; label: string }[]>([]);
    const [showcases, setShowcases] = useState<Showcase[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [loadingShowcases, setLoadingShowcases] = useState(true);
    const [saving, setSaving] = useState(false);
    const [alertInfo, setAlertInfo] = useState<{
        show: boolean;
        type: "success" | "error" | "warning" | "info";
        title: string;
        message: string;
    } | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Busca produtos
    useEffect(() => {
        const fetchProducts = async () => {
            setLoadingProducts(true);
            try {
                const response = await api.get("/api/products", {
                    params: { companyId },
                });
                const rawProducts: Product[] =
                    response.data?.products ||
                    response.data?.data ||
                    (Array.isArray(response.data) ? response.data : []);

                setProducts(
                    rawProducts.map((p) => ({ value: p.id, label: p.name }))
                );
            } catch {
                setProducts([]);
            } finally {
                setLoadingProducts(false);
            }
        };

        fetchProducts();
    }, [companyId]);

    // Busca vitrines
    useEffect(() => {
        const fetchShowcases = async () => {
            setLoadingShowcases(true);
            try {
                const response = await api.get("/api/members/showcases", {
                    params: { companyId },
                });
                setShowcases(response.data?.data || []);
            } catch {
                setShowcases([]);
            } finally {
                setLoadingShowcases(false);
            }
        };

        fetchShowcases();
    }, [companyId]);

    // Busca ofertas do produto selecionado
    const fetchProductOffers = async (selectedProductId: string) => {
        setLoadingOffers(true);
        setOffers([]);
        setSelectedOffers([]);
        setSelectAllOffers(false);
        setOffersList([]);
        try {
            const resp = await api.get(
                `/api/products/${selectedProductId}/offers-with-links`
            );
            if (resp.data && resp.data.offers && Array.isArray(resp.data.offers)) {
                const mappedOffers = (resp.data.offers as OfferApi[]).map((offer) => ({
                    value: offer.id,
                    label: offer.name,
                }));
                setOffers(mappedOffers);
            }
        } catch {
            setOffers([]);
        } finally {
            setLoadingOffers(false);
        }
    };

    const handleProductChange = async (value: string) => {
        setProductId(value);
        setErrors((prev) => ({ ...prev, productId: "", offersList: "" }));
        setOffersList([]);
        setSelectedOffers([]);
        setSelectAllOffers(false);
        if (value) {
            await fetchProductOffers(value);
        }
    };

    const handleOffersChange = (values: string[]) => {
        const isAllSelected = values.includes("all");
        setErrors((prev) => ({ ...prev, offersList: "" }));
        if (isAllSelected) {
            setSelectAllOffers(true);
            setSelectedOffers(["all"]);
            setOffersList([]);
        } else {
            setSelectAllOffers(false);
            setSelectedOffers(values);
            setOffersList(values);
        }
    };

    const handleShowcasesChange = (values: string[]) => {
        setSelectedShowcaseIds(values);
        setErrors((prev) => ({ ...prev, showcases: "" }));
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!name.trim()) newErrors.name = "Nome da integração é obrigatório";
        if (!productId) newErrors.productId = "Selecione um produto";
        if (!selectAllOffers && offersList.length === 0)
            newErrors.offersList = "Selecione ao menos uma oferta ou 'Qualquer oferta'";
        if (selectedShowcaseIds.length === 0)
            newErrors.showcases = "Selecione ao menos uma vitrine";
        if (!defaultPassword.trim()) newErrors.defaultPassword = "A senha padrão é obrigatória";
        if (defaultPassword.trim().length < 6) newErrors.defaultPassword = "A senha deve ter ao menos 6 caracteres";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setSaving(true);
        try {
            const selectedShowcases = showcases.filter((s) =>
                selectedShowcaseIds.includes(s.showcaseId)
            );
            const showcaseNames = selectedShowcases.map((s) => s.name);

            await api.post("/api/membership", {
                provider: "sellyo_members",
                name: name.trim(),
                productId,
                offersList,
                active,
                settingsData: {
                    showcaseIds: selectedShowcaseIds,
                    showcaseNames,
                    defaultPassword: defaultPassword.trim(),
                },
            });

            setAlertInfo({
                show: true,
                type: "success",
                title: "Sucesso!",
                message: "Integração criada com sucesso.",
            });
            setTimeout(() => navigate("/apps/sellyo-members"), 1500);
        } catch (err: unknown) {
            const message = axios.isAxiosError(err)
                ? err.response?.data?.error || err.message
                : err instanceof Error
                    ? err.message
                    : "Erro ao salvar integração.";
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: message || "Erro ao salvar integração.",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleGoBack = () => navigate("/apps/sellyo-members");

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="col-span-1 lg:col-span-2">
                <ComponentCard title="Nova Integração — Sellyo Members">
                    {alertInfo?.show && (
                        <div className="mt-4">
                            <Alert
                                variant={alertInfo.type}
                                title={alertInfo.title}
                                message={alertInfo.message}
                            />
                        </div>
                    )}

                    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Switch
                                label="Ativar integração"
                                checked={active}
                                onChange={(checked) => setActive(checked)}
                            />
                        </div>

                        {/* Nome */}
                        <div className="mt-5">
                            <Label htmlFor="name">Nome da integração *</Label>
                            <InputField
                                type="text"
                                id="name"
                                placeholder="Informe um nome para identificação"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    setErrors((prev) => ({ ...prev, name: "" }));
                                }}
                                error={!!errors.name}
                                hint={errors.name}
                            />
                        </div>

                        {/* Produto */}
                        <div className="mt-5">
                            <Label>Produto *</Label>
                            <Select
                                options={products}
                                placeholder="Selecione um produto"
                                onChange={handleProductChange}
                                value={productId}
                                className={errors.productId ? "border-red-500" : ""}
                            />
                            {errors.productId && (
                                <p className="mt-1 text-sm text-red-500">{errors.productId}</p>
                            )}
                            {loadingProducts && (
                                <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                    Carregando produtos...
                                </div>
                            )}
                        </div>

                        {/* Ofertas (aparece após selecionar produto) */}
                        {productId && (
                            <div className="mt-5">
                                <Label>Ofertas *</Label>
                                <MultiSelectCheckbox
                                    options={[
                                        { id: "all", label: "Qualquer oferta" },
                                        ...offers.map((offer) => ({
                                            id: offer.value,
                                            label: offer.label,
                                        })),
                                    ]}
                                    selectedValues={selectedOffers}
                                    onChange={handleOffersChange}
                                    placeholder="Selecione as ofertas"
                                    showSelectAll={false}
                                    className={errors.offersList ? "border-red-500" : ""}
                                />
                                {errors.offersList && (
                                    <p className="mt-1 text-sm text-red-500">{errors.offersList}</p>
                                )}
                                {loadingOffers && (
                                    <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                        Carregando ofertas...
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Vitrines */}
                        <div className="mt-5">
                            <Label>Vitrines liberadas ao comprar *</Label>
                            <MultiSelectCheckbox
                                options={showcases.map((s) => ({
                                    id: s.showcaseId,
                                    label: s.name,
                                }))}
                                selectedValues={selectedShowcaseIds}
                                onChange={handleShowcasesChange}
                                placeholder="Selecione as vitrines"
                                showSelectAll={false}
                                className={errors.showcases ? "border-red-500" : ""}
                            />
                            {errors.showcases && (
                                <p className="mt-1 text-sm text-red-500">{errors.showcases}</p>
                            )}
                            {loadingShowcases && (
                                <div className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400">
                                    <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-primary rounded-full"></div>
                                    Carregando vitrines...
                                </div>
                            )}
                        </div>

                        {/* Senha padrão */}
                        <div className="mt-5">
                            <Label htmlFor="defaultPassword">Senha padrão do aluno *</Label>
                            <div className="relative">
                                <InputField
                                    type={showPassword ? "text" : "password"}
                                    id="defaultPassword"
                                    placeholder="Senha que o aluno receberá no primeiro acesso"
                                    value={defaultPassword}
                                    onChange={(e) => {
                                        setDefaultPassword(e.target.value);
                                        setErrors((prev) => ({ ...prev, defaultPassword: "" }));
                                    }}
                                    error={!!errors.defaultPassword}
                                    hint={errors.defaultPassword}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                                    title={showPassword ? "Ocultar senha" : "Mostrar senha"}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-500" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-gray-500" />
                                    )}
                                </button>
                            </div>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Essa será a senha enviada ao aluno no e-mail de primeiro acesso. Ele poderá alterá-la depois.
                            </p>
                        </div>

                        {/* Ações */}
                        <div className="mt-6 flex justify-end gap-4.5">
                            <Button size="md" variant="outline" onClick={handleGoBack}>
                                Cancelar
                            </Button>
                            <Button
                                size="md"
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={saving}
                            >
                                {saving ? "Salvando..." : "Salvar integração"}
                            </Button>
                        </div>
                    </form>
                </ComponentCard>
            </div>
        </div>
    );
}